import { Extension } from '@tiptap/core';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

const PAGE_HEIGHT = 1120; // px — altura total da página (deve coincidir com .page min-height no CSS)
const PAGE_PADDING = 72;  // px — padding top+bottom da .page (72px top + 72px bottom)
const CONTENT_HEIGHT = PAGE_HEIGHT - PAGE_PADDING * 2; // 976px de conteúdo útil

const paginationPluginKey = new PluginKey('pagination');

function createPageBreakSpacer(): HTMLElement {
  const spacer = document.createElement('div');
  spacer.className = 'page-break-spacer';
  spacer.setAttribute('contenteditable', 'false');
  return spacer;
}

function getBlockMargins(el: HTMLElement): { top: number; bottom: number } {
  const style = window.getComputedStyle(el);
  return {
    top: parseFloat(style.marginTop) || 0,
    bottom: parseFloat(style.marginBottom) || 0,
  };
}

export const Pagination = Extension.create({
  name: 'pagination',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: paginationPluginKey,

        state: {
          init() {
            return DecorationSet.empty;
          },
          apply(tr, oldDecos) {
            // Se o metadata 'paginationDecos' existir, usar
            const decos = tr.getMeta(paginationPluginKey);
            if (decos !== undefined) {
              return decos;
            }
            // Se o doc mudou, mapear decorações existentes (serão recalculadas no view update)
            if (tr.docChanged) {
              return oldDecos.map(tr.mapping, tr.doc);
            }
            return oldDecos;
          },
        },

        view(editorView) {
          let isUpdating = false;
          let rafId: number | null = null;
          let prevBreakPositions: number[] = [];

          function arraysEqual(a: number[], b: number[]): boolean {
            if (a.length !== b.length) return false;
            for (let i = 0; i < a.length; i++) {
              if (a[i] !== b[i]) return false;
            }
            return true;
          }

          function recalculate() {
            if (isUpdating) return;

            const { state } = editorView;
            const { doc } = state;
            const breakPositions: number[] = [];

            let accumulatedHeight = 0;
            let prevMarginBottom = 0;

            doc.forEach((node, offset) => {
              // Localizar o elemento DOM do bloco
              const domPos = editorView.domAtPos(offset + 1);
              let domNode = domPos.node;

              // Navegar até o elemento do bloco (pode retornar text node)
              if (domNode.nodeType === Node.TEXT_NODE) {
                domNode = domNode.parentElement!;
              }

              // Subir até encontrar o elemento de bloco direto do editor
              const editorDom = editorView.dom;
              while (domNode && domNode.parentElement !== editorDom) {
                domNode = domNode.parentElement!;
              }

              if (!domNode || !(domNode instanceof HTMLElement)) return;

              const el = domNode as HTMLElement;
              const margins = getBlockMargins(el);
              const blockHeight = el.offsetHeight;

              // Margin collapsing: usar o maior entre margin-bottom anterior e margin-top atual
              const collapsedMargin = Math.max(prevMarginBottom, margins.top);

              // Primeira iteração não adiciona margem superior
              const totalBlockSpace =
                accumulatedHeight === 0
                  ? blockHeight
                  : collapsedMargin + blockHeight;

              if (
                accumulatedHeight > 0 &&
                accumulatedHeight + totalBlockSpace > CONTENT_HEIGHT
              ) {
                // Quebra de página antes deste bloco
                // A posição é o início do node no documento
                breakPositions.push(offset + 1);

                // Resetar acumulador — este bloco começa na nova página
                accumulatedHeight = blockHeight;
              } else {
                accumulatedHeight += totalBlockSpace;
              }

              prevMarginBottom = margins.bottom;
            });

            // Anti-loop: só despachar se as posições mudaram
            if (arraysEqual(prevBreakPositions, breakPositions)) {
              return;
            }
            prevBreakPositions = breakPositions;

            // Criar decorações
            const decorations: Decoration[] = breakPositions.map((pos) =>
              Decoration.widget(pos, createPageBreakSpacer, {
                side: -1,
                key: `page-break-${pos}`,
              })
            );

            const decoSet = DecorationSet.create(doc, decorations);

            isUpdating = true;
            const tr = editorView.state.tr;
            tr.setMeta(paginationPluginKey, decoSet);
            tr.setMeta('addToHistory', false);
            editorView.dispatch(tr);
            isUpdating = false;
          }

          function scheduleRecalc() {
            if (rafId !== null) {
              cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
              rafId = null;
              recalculate();
            });
          }

          // ResizeObserver para recalcular ao redimensionar
          const resizeObserver = new ResizeObserver(() => {
            scheduleRecalc();
          });
          resizeObserver.observe(editorView.dom);

          // Calcular na inicialização
          scheduleRecalc();

          return {
            update() {
              scheduleRecalc();
            },
            destroy() {
              if (rafId !== null) {
                cancelAnimationFrame(rafId);
              }
              resizeObserver.disconnect();
            },
          };
        },

        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});
